(function () {
  const PRODUCT_ENDPOINT =
    "https://etempashopifyapps-server-a.co.uk/endpoints/bulk_offers/fetch_single_product_bulk_offers.php";
  const CART_ENDPOINT =
    "https://etempashopifyapps-server-a.co.uk/endpoints/bulk_offers/fetch_bulk_offers.php";

  const postJson = (url, payload, signal) =>
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
      signal,
    }).then((response) => {
      if (!response.ok) throw new Error("Bulk offers request failed");
      return response.json();
    });

  const normaliseId = (value) => String(value || "");

  const createStrongText = (text) => {
    const strong = document.createElement("strong");
    strong.textContent = text;
    return strong;
  };

  class BulkOfferBox extends HTMLElement {
    connectedCallback() {
      this.productId = this.dataset.productId;
      this.variantId = this.dataset.variantId;
      this.abortController = null;
      this.onVariantChange = this.onVariantChange.bind(this);
      document.addEventListener("variant-change-global", this.onVariantChange);
      this.fetchOffers();
    }

    disconnectedCallback() {
      document.removeEventListener("variant-change-global", this.onVariantChange);
      if (this.abortController) this.abortController.abort();
    }

    onVariantChange(event) {
      const variantId = event.detail && event.detail.id;
      if (!variantId || normaliseId(variantId) === normaliseId(this.variantId)) return;
      this.variantId = variantId;
      this.dataset.variantId = variantId;
      this.fetchOffers();
    }

    fetchOffers() {
      if (!this.productId || !this.variantId) {
        this.replaceChildren();
        return;
      }

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      postJson(
        PRODUCT_ENDPOINT,
        {
          product_id: Number(this.productId),
          variant_id: Number(this.variantId),
        },
        this.abortController.signal,
      )
        .then((data) => {
          if (data.failed) {
            this.replaceChildren();
            return;
          }

          const discount = Array.isArray(data.bulk_discounts)
            ? data.bulk_discounts[0]
            : null;
          const offers = discount && Array.isArray(discount.offers) ? discount.offers : [];

          if (!offers.length) {
            this.replaceChildren();
            return;
          }

          this.renderOffers(offers);
        })
        .catch((error) => {
          if (error.name !== "AbortError") this.replaceChildren();
        });
    }

    renderOffers(offers) {
      const box = document.createElement("div");
      box.className = "bulk-discount-box";

      const header = document.createElement("div");
      header.className = "bulk-discount-header";
      header.textContent = "Save when you buy in larger quantities!";
      box.appendChild(header);

      const tiers = document.createElement("div");
      tiers.className = "bulk-discount-tiers";

      offers.forEach((offer) => {
        if (!offer.quantity || !offer.percent) return;

        const tier = document.createElement("p");
        tier.appendChild(createStrongText(`Buy ${offer.quantity}+`));
        tier.append(" get ");
        tier.appendChild(createStrongText(`${offer.percent}% off`));
        tiers.appendChild(tier);
      });

      if (!tiers.children.length) {
        this.replaceChildren();
        return;
      }

      box.appendChild(tiers);

      const footer = document.createElement("p");
      footer.className = "bulk-discount-footer";
      footer.append("Discounts ");
      footer.appendChild(createStrongText("apply automatically at checkout."));
      box.appendChild(footer);

      this.replaceChildren(box);
    }
  }

  if (!customElements.get("bulk-offer-box")) {
    customElements.define("bulk-offer-box", BulkOfferBox);
  }

  let cartAbortController = null;

  const clearCartNotices = (scope) => {
    scope.querySelectorAll("[data-bulk-cart-offer-notice]").forEach((notice) => {
      notice.replaceChildren();
      notice.hidden = true;
    });
  };

  const renderCartNotice = (notice, offer) => {
    if (!offer || !offer.quantity || !offer.percent) return;

    const wrapper = document.createElement("div");
    wrapper.className = "bulk-offer-cart-notice";

    const iconTemplate = document.getElementById("BulkOfferCartNoticeIcon");
    if (iconTemplate) {
      const icon = iconTemplate.content.cloneNode(true);
      const svg = icon.querySelector("svg");
      if (svg) {
        svg.setAttribute("aria-hidden", "true");
        svg.setAttribute("focusable", "false");
      }
      wrapper.appendChild(icon);
    }

    const text = document.createElement("span");
    text.textContent = `Buy ${offer.quantity}+ and save ${offer.percent}%`;
    wrapper.appendChild(text);

    notice.replaceChildren(wrapper);
    notice.hidden = false;
  };

  window.refreshCartBulkOfferNotices = function refreshCartBulkOfferNotices(scope) {
    const root =
      scope ||
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer") ||
      document;
    const lines = Array.from(root.querySelectorAll("[data-bulk-cart-line]"));

    if (!lines.length) return;

    clearCartNotices(root);

    const items = lines
      .map((line) => ({
        product_id: Number(line.dataset.bulkProductId),
        variant_id: Number(line.dataset.bulkVariantId),
        quantity: Number(line.dataset.bulkQuantity),
      }))
      .filter((item) => item.product_id && item.variant_id && item.quantity);

    if (!items.length) return;

    if (cartAbortController) cartAbortController.abort();
    cartAbortController = new AbortController();

    postJson(CART_ENDPOINT, { items }, cartAbortController.signal)
      .then((data) => {
        if (data.failed || !Array.isArray(data.bulk_discounts)) {
          clearCartNotices(root);
          return;
        }

        const discountsByLine = new Map();
        data.bulk_discounts.forEach((discount) => {
          if (!discount || !discount.next_offer) return;
          discountsByLine.set(
            `${normaliseId(discount.product_id)}:${normaliseId(discount.variant_id)}`,
            discount.next_offer,
          );
        });

        lines.forEach((line) => {
          const notice = line.querySelector("[data-bulk-cart-offer-notice]");
          if (!notice) return;

          const key = `${normaliseId(line.dataset.bulkProductId)}:${normaliseId(line.dataset.bulkVariantId)}`;
          const offer = discountsByLine.get(key);

          if (offer) renderCartNotice(notice, offer);
        });
      })
      .catch((error) => {
        if (error.name !== "AbortError") clearCartNotices(root);
      });
  };

  document.addEventListener("DOMContentLoaded", () => {
    window.refreshCartBulkOfferNotices();
  });
})();
